import React, { useState } from 'react';
import { Check } from 'lucide-react';
import MerkLogo from './MerkLogo';

export default function PrivacyPolicyPage({ onAccepted, brand }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    setLoading(true);
    setError('');
    try {
      await onAccepted();
    } catch (err) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          {/* Nooit het platformlogo bij een partnerrol (CLAUDE.md): dit scherm komt
              meteen na de eerste aanmelding, dus juist waar het merk moet kloppen. */}
          <MerkLogo brand={brand} className="h-12 object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Privacybeleid</h1>
            <p className="text-slate-400 text-sm">Partner Portal — AVG/GDPR</p>
          </div>
        </div>

        <div className="space-y-5 text-slate-300 text-sm max-h-[60vh] overflow-y-auto pr-2">
          <section>
            <h2 className="text-lg font-semibold text-slate-100 mb-2">1. Welke gegevens verwerken wij?</h2>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li>Naam en e-mailadres (voor account en communicatie)</li>
              <li>Wachtwoord (versleuteld opgeslagen, nooit leesbaar)</li>
              <li>MFA-configuratie (voor tweefactorauthenticatie)</li>
              <li>IP-adres en tijdstip van inloggen (voor beveiliging)</li>
              <li>Activiteitenlogboek (audit trail voor compliance)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-100 mb-2">2. Doel van de verwerking</h2>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li>Toegangsbeheer tot het Partner Portal</li>
              <li>Beveiliging van uw account en onze systemen</li>
              <li>Naleving van wettelijke verplichtingen (AVG/GDPR)</li>
              <li>Audit en traceerbaarheid van acties</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-100 mb-2">3. Bewaartermijn</h2>
            <p className="text-slate-400">
              Uw persoonsgegevens worden bewaard zolang uw account actief is. Audit logs worden maximaal 2 jaar bewaard.
              Na verwijdering van uw account worden persoonlijke gegevens verwijderd en audit logs geanonimiseerd.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-100 mb-2">4. Uw rechten</h2>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li><strong className="text-slate-300">Inzage:</strong> U kunt uw gegevens exporteren via Account instellingen</li>
              <li><strong className="text-slate-300">Rectificatie:</strong> Neem contact op met de beheerder voor wijzigingen</li>
              <li><strong className="text-slate-300">Verwijdering:</strong> U kunt uw account zelf verwijderen via Account instellingen</li>
              <li><strong className="text-slate-300">Dataportabiliteit:</strong> Exporteer uw gegevens als JSON-bestand</li>
              <li><strong className="text-slate-300">Bezwaar:</strong> Neem contact op met de verwerkingsverantwoordelijke</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-100 mb-2">5. Beveiliging</h2>
            <p className="text-slate-400">
              Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen,
              waaronder versleutelde wachtwoorden, tweefactorauthenticatie (MFA), en beveiligde verbindingen (HTTPS).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-100 mb-2">6. Contact</h2>
            <p className="text-slate-400">
              Voor vragen over dit privacybeleid of uw rechten kunt u contact opnemen met de verwerkingsverantwoordelijke
              via het e-mailadres van uw organisatie.
            </p>
          </section>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-slate-700/50">
          <p className="text-slate-400 text-sm mb-4">
            Door op "Akkoord" te klikken, bevestigt u dat u dit privacybeleid heeft gelezen en akkoord gaat met de verwerking van uw gegevens.
          </p>
          <button
            onClick={handleAccept}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-lime-500 hover:bg-lime-400 disabled:bg-lime-500/50 text-slate-900 font-semibold rounded-xl transition-all"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Check size={20} />
                Akkoord — Ik heb het privacybeleid gelezen en ga akkoord
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
